import React, { useId } from "react";
import { Box, Typography } from "@mui/material";
import { linePaths, ringSlicePath } from "../../utils/kitPresentation";
import { tokens } from "../../utils/figmaTokens";

// The Performance metrics donut (Teacher overview 10982:85741): a 240 px ring, 36 px thick, on
// the track grey. Each segment ({ key, tone, fraction }, tutorDetail.performanceSegments) is a
// slice clockwise from 12 o'clock with Figma's 2 px seams; what no segment covers stays grey.
// The label and the total sit in the middle. Plain SVG: the numbers are written beside it.
export const Donut = ({ segments, label, value }) => {
  let from = 0;
  return (
    <Box sx={{ position: "relative", width: 240, height: 240, flexShrink: 0 }}>
      <svg width={240} height={240} viewBox="0 0 240 240" overflow="visible" aria-hidden focusable="false" style={{ display: "block" }}>
        <circle cx={120} cy={120} r={102} fill="none" stroke={tokens.chart.track} strokeWidth={36} />
        {segments.map((s) => {
          const d = ringSlicePath(120, 120, 84, 120, from, from + s.fraction);
          from += s.fraction;
          return <path key={s.key} d={d} fill={tokens.chart[s.tone]} stroke={tokens.chart.seam} strokeWidth={2} />;
        })}
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="bodySmallStrong" sx={{ color: tokens.chart.label }}>{label}</Typography>
        <Typography variant="titleMetric" sx={{ color: tokens.text.primary }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

// The Feedback rate gauge (10982:85773): the top half of a 181 px ring, 13.575 px thick, on the
// track grey, with the value running clockwise from 9 o'clock in Figma's gradient. `rate` is
// 0–100, or null for no value arc. The gradient's id is unique per gauge.
export const HalfGauge = ({ rate, value, label }) => {
  const gradientId = `gauge${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const outer = 90.5;
  const inner = outer - 13.575;
  return (
    <Box sx={{ position: "relative", width: 181, height: 90.5, flexShrink: 0 }}>
      <svg width={181} height={90.5} viewBox="0 0 181 90.5" aria-hidden focusable="false" style={{ display: "block" }}>
        <defs>
          <linearGradient id={gradientId} x1={5.5} y1={91} x2={173.5} y2={70} gradientUnits="userSpaceOnUse">
            {tokens.chart.gauge.map((stop) => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>
        </defs>
        <path d={ringSlicePath(outer, outer, inner, outer, 0.75, 1.25)} fill={tokens.chart.track} />
        {rate > 0 && <path d={ringSlicePath(outer, outer, inner, outer, 0.75, 0.75 + rate / 200)} fill={`url(#${gradientId})`} />}
      </svg>
      <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography sx={{ fontSize: 21, lineHeight: "28px", fontWeight: 600, letterSpacing: "-0.2px", color: tokens.text.primary }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: 10, lineHeight: "14px", fontWeight: 600, letterSpacing: "0.3px", color: tokens.text.tertiary }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

const gradientIdOf = (id) => id.replace(/[^a-zA-Z0-9_-]/g, "");

// A Reports card's trend line (Figma "KPI · …", 11521:96836; 044 D85): 44 px tall across the
// card, a 2.6 px line over a fill that fades down. Decorative — the number is written above it,
// so it hides from screen readers. Needs two points.
export const Sparkline = ({ points, color }) => {
  const fillId = `spark${gradientIdOf(useId())}`;
  const { line, area } = linePaths(points, Math.max(0, ...points) * 1.1);
  return (
    <svg
      width="100%"
      height={44}
      viewBox={`0 0 ${Math.max(1, points.length - 1)} 100`}
      preserveAspectRatio="none"
      overflow="visible"
      aria-hidden
      focusable="false"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity={0.16} />
          <stop offset="1" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${fillId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={2.6} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

